import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type EspecialidadLocal } from '../datos/db.js'
import { sincronizar } from '../datos/sync.js'
import { Boton } from '../comunes/Boton.js'

/**
 * Especialidades en Configuración (T30e).
 *
 * Tres piezas sobre el mismo catálogo:
 *
 *   - `CatalogoEspecialidades`: la lista de la clínica (alta, renombrar, borrar).
 *   - `EspecialidadesProfesional`: con cuáles atiende cada profesional.
 *   - `SelectorEspecialidadRequerida`: cuál le exige cada servicio al agendar.
 *
 * **Lee todo de Dexie** (§5.5), como `DuracionesProfesional.tsx`: el catálogo y
 * los vínculos viajan por el canal operativo (T30d), así que acá no hay ni un
 * `fetch` de lectura. Lo único que va por la red son las escrituras —el POST del
 * alta, el PATCH del renombre, el DELETE y el PUT del conjunto—, y después de
 * cada una un `sincronizar()`: el pull repinta Dexie solo y la lista muestra el
 * cambio sin que nadie tenga que recargar la pantalla.
 *
 * Dos detalles del contrato que no son evidentes al mirar la UI:
 *
 *   - El `PUT /config/recursos/:id/especialidades` **reemplaza** el conjunto, no
 *     lo agrega: lo que no va en `especialidadIds` queda desvinculado. Por eso
 *     el botón dice "Guardar" y no "Agregar".
 *   - Borrar una especialidad es un borrado lógico **sin vuelta atrás desde acá**.
 *     Si un servicio vivo la exige, el servidor responde 409 `en_uso` y no borra
 *     nada: por eso el borrado pregunta antes, y por eso el error dice qué
 *     hacer en lugar de dejar un mensaje seco.
 */

const API = '/api'

/** Un error de la API, ya traducido para pintar: `mensaje` y el `detalle.motivo`. */
interface Falla {
  mensaje: string
  /** Sólo en los 409 del catálogo: `duplicado` o `en_uso`. */
  motivo?: string
}

/**
 * Escribe contra la API. Devuelve la respuesta cruda: cada acción decide qué
 * hacer con el `detalle`, que es distinto para un 409 que para un 404.
 */
async function escribir(metodo: string, ruta: string, cuerpo?: unknown): Promise<Response> {
  return fetch(`${API}${ruta}`, {
    method: metodo,
    credentials: 'include',
    ...(cuerpo === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(cuerpo) }),
  })
}

/** Toda respuesta de error trae `{ error, mensaje, detalle? }` (`apps/api/src/errores.ts`). */
async function leerFalla(res: Response, porDefecto: string): Promise<Falla> {
  const cuerpo = (await res.json().catch(() => ({}))) as {
    mensaje?: string
    detalle?: { motivo?: string }
  }
  return {
    mensaje: cuerpo.mensaje ?? porDefecto,
    ...(cuerpo.detalle?.motivo ? { motivo: cuerpo.detalle.motivo } : {}),
  }
}

/** El catálogo se ordena como lo muestra la API y como lo espera el ojo: `es-PY`. */
function porNombre(lista: EspecialidadLocal[]): EspecialidadLocal[] {
  return [...lista].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es-PY'))
}

/** El catálogo de especialidades de la clínica: el maestro del que todo lo demás cuelga. */
export function CatalogoEspecialidades() {
  const catalogo = useLiveQuery(() => db.especialidad.toArray(), [], null)
  const [abierto, setAbierto] = useState(false)
  const [nombre, setNombre] = useState('')
  const [editando, setEditando] = useState<string | null>(null)
  const [valorEdicion, setValorEdicion] = useState('')
  const [borrando, setBorrando] = useState<string | null>(null)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<Falla | null>(null)

  async function crear() {
    if (nombre.trim() === '') return
    setGuardando(true)
    setError(null)
    try {
      const res = await escribir('POST', '/config/especialidades', { nombre })
      if (!res.ok) {
        setError(await leerFalla(res, 'No pudimos agregar la especialidad'))
        return
      }
      // El alta no cierra el formulario: el catálogo se carga casi siempre de a
      // varias, y reabrir por cada una es el trabajo de más.
      setNombre('')
      void sincronizar()
    } catch {
      setError({ mensaje: 'No pudimos agregar la especialidad' })
    } finally {
      setGuardando(false)
    }
  }

  async function renombrar(id: string) {
    if (valorEdicion.trim() === '') {
      setError({ mensaje: 'El nombre no puede quedar vacío' })
      return
    }
    setGuardando(true)
    setError(null)
    try {
      const res = await escribir('PATCH', `/config/especialidades/${id}`, { nombre: valorEdicion })
      if (!res.ok) {
        setError(await leerFalla(res, 'No pudimos renombrar la especialidad'))
        return
      }
      setEditando(null)
      void sincronizar()
    } catch {
      setError({ mensaje: 'No pudimos renombrar la especialidad' })
    } finally {
      setGuardando(false)
    }
  }

  async function borrar(id: string) {
    setGuardando(true)
    setError(null)
    try {
      const res = await escribir('DELETE', `/config/especialidades/${id}`)
      if (!res.ok) {
        setError(await leerFalla(res, 'No pudimos borrar la especialidad'))
        return
      }
      setBorrando(null)
      void sincronizar()
    } catch {
      setError({ mensaje: 'No pudimos borrar la especialidad' })
    } finally {
      setGuardando(false)
    }
  }

  return (
    <section className="mt-6 border-t border-slate-200 pt-4">
      <h2 className="txt-seccion">Especialidades</h2>
      <p className="txt-secundario mb-3">
        Catálogo de la clínica. Con esto se marca qué atiende cada profesional y qué especialidad le
        exige un servicio al que se quiera agendar.
      </p>

      {catalogo === null ? (
        <p className="text-sm text-slate-400">Leyendo…</p>
      ) : catalogo.length === 0 ? (
        <p className="text-sm text-slate-500">Todavía no hay especialidades cargadas.</p>
      ) : (
        <ul className="space-y-1">
          {porNombre(catalogo).map((e) => (
            <li key={e.id} className="text-sm">
              {editando === e.id ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    autoFocus
                    value={valorEdicion}
                    onChange={(ev) => setValorEdicion(ev.target.value)}
                    maxLength={80}
                    aria-label={`Nuevo nombre de ${e.nombre}`}
                    className="min-h-8 flex-1 rounded-md border border-slate-300 px-2 text-[14px]"
                  />
                  <Boton
                    variante="secondary"
                    tamano="sm"
                    onClick={() => void renombrar(e.id)}
                    disabled={guardando}
                  >
                    Guardar
                  </Boton>
                  <Boton variante="ghost" tamano="sm" onClick={() => setEditando(null)}>
                    Cancelar
                  </Boton>
                </div>
              ) : (
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="font-medium text-slate-800">{e.nombre}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setEditando(e.id)
                      setValorEdicion(e.nombre)
                      setBorrando(null)
                    }}
                    className="ml-auto text-sm text-teal-700"
                  >
                    Renombrar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBorrando(e.id)
                      setEditando(null)
                    }}
                    className="text-sm text-slate-500"
                  >
                    Borrar
                  </button>
                </div>
              )}

              {/* Borrar es lógico y no se puede deshacer desde acá: pregunta primero. */}
              {borrando === e.id && (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="text-[13px] text-slate-600">¿Borrar «{e.nombre}»?</span>
                  <Boton
                    variante="danger"
                    tamano="sm"
                    onClick={() => void borrar(e.id)}
                    disabled={guardando}
                  >
                    Sí, borrar
                  </Boton>
                  <Boton variante="ghost" tamano="sm" onClick={() => setBorrando(null)}>
                    Cancelar
                  </Boton>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="mt-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
          {error.mensaje}
          {error.motivo === 'en_uso' && (
            <span className="mt-1 block text-xs">
              Sacala primero del servicio que la exige: borrarla no desvincula los servicios.
            </span>
          )}
        </p>
      )}

      {abierto ? (
        <form
          className="mt-3 rounded-lg border border-slate-200 p-3"
          onSubmit={(ev) => {
            ev.preventDefault()
            void crear()
          }}
        >
          <label className="block text-sm">
            Nombre
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
              maxLength={80}
              autoFocus
              placeholder="Ortodoncia, Endodoncia…"
              className="mt-1 min-h-10 w-full rounded-md border border-slate-300 px-2"
            />
          </label>
          <div className="mt-3 flex gap-2">
            <Boton variante="primary" type="submit" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Agregar'}
            </Boton>
            <Boton variante="ghost" onClick={() => setAbierto(false)}>
              Cancelar
            </Boton>
          </div>
        </form>
      ) : (
        <Boton variante="secondary" className="mt-3" onClick={() => setAbierto(true)}>
          Agregar especialidad
        </Boton>
      )}
    </section>
  )
}

export interface EspecialidadesProfesionalProps {
  personaId: string
}

/**
 * Las especialidades de **esta** persona. Se muestra sólo a quien el paciente
 * elige, igual que `DuracionesProfesional`: es el único tipo de persona con
 * agenda propia por servicio, y el único al que un servicio le puede exigir una
 * especialidad.
 */
export function EspecialidadesProfesional({ personaId }: EspecialidadesProfesionalProps) {
  const catalogo = useLiveQuery(() => db.especialidad.toArray(), [], null)
  const vinculadas = useLiveQuery(
    () => db.persona_especialidad.where('persona_id').equals(personaId).toArray(),
    [personaId],
    null,
  )

  const [elegidas, setElegidas] = useState<string[]>([])
  const [guardando, setGuardando] = useState(false)
  const [guardado, setGuardado] = useState(false)
  const [error, setError] = useState<Falla | null>(null)
  // Hay cambios del usuario sin guardar: el pull no los pisa hasta que se guarden.
  const [sucio, setSucio] = useState(false)

  // Dexie manda y sólo se reacomoda el input cuando *el pull* trae otra cosa: si
  // se resincronizara en cada render, marcar una casilla se pelearía con el
  // guardado. Por eso el disparador es el texto de los ids, no el array.
  // El "Guardado" no se limpia acá: es la respuesta a lo que se acaba de hacer,
  // y el pull que confirma ese mismo cambio no puede borrarla.
  const delServidor = (vinculadas ?? [])
    .map((v) => v.especialidad_id)
    .sort()
    .join(',')
  useEffect(() => {
    if (sucio) return
    setElegidas(delServidor === '' ? [] : delServidor.split(','))
  }, [delServidor, sucio])

  const alternar = (id: string) => {
    setElegidas((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    setSucio(true)
    setGuardado(false)
  }

  async function guardar() {
    setGuardando(true)
    setGuardado(false)
    setError(null)
    try {
      // El conjunto completo, no un delta: el endpoint reemplaza lo que haya.
      const res = await escribir('PUT', `/config/recursos/${personaId}/especialidades`, {
        especialidadIds: elegidas,
      })
      if (!res.ok) {
        setError(await leerFalla(res, 'No pudimos guardar las especialidades'))
        return
      }
      setGuardado(true)
      setSucio(false)
      void sincronizar()
    } catch {
      setError({ mensaje: 'No pudimos guardar las especialidades' })
    } finally {
      setGuardando(false)
    }
  }

  return (
    <section className="mt-4 border-t border-slate-100 pt-4">
      <h3 className="text-sm font-semibold text-slate-900">Especialidades</h3>
      <p className="txt-secundario mb-3">
        Con cuáles atiende este profesional. Un servicio que exige una especialidad sólo se agenda
        con quien la tiene.
      </p>

      {catalogo === null || vinculadas === null ? (
        <p className="text-sm text-slate-400">Leyendo…</p>
      ) : catalogo.length === 0 ? (
        <p className="text-sm text-slate-500">
          No hay especialidades cargadas. Agregá una en la sección de arriba.
        </p>
      ) : (
        <>
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {porNombre(catalogo).map((e) => (
              <li key={e.id}>
                <label className="text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={elegidas.includes(e.id)}
                    onChange={() => alternar(e.id)}
                    // Bloqueadas mientras se guarda: un cambio a mitad del PUT no se enviaría
                    // y el "Guardado" mentiría.
                    disabled={guardando}
                  />{' '}
                  {e.nombre}
                </label>
              </li>
            ))}
          </ul>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Boton
              variante="primary"
              tamano="sm"
              onClick={() => void guardar()}
              disabled={guardando}
            >
              {guardando ? 'Guardando…' : 'Guardar'}
            </Boton>

            {guardado && <span role="status" className="text-[13px] text-teal-700">Guardado</span>}
            {error && (
              <span role="alert" className="text-[13px] text-rose-700">
                {error.mensaje}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  )
}

export interface SelectorEspecialidadRequeridaProps {
  /** `null` = el servicio se agenda con cualquiera. */
  valor: string | null
  onCambiar: (especialidadId: string | null) => void
}

/** La especialidad que un servicio exige. `Ninguna` = no filtra por especialidad. */
export function SelectorEspecialidadRequerida({
  valor,
  onCambiar,
}: SelectorEspecialidadRequeridaProps) {
  const catalogo = useLiveQuery(() => db.especialidad.toArray(), [], null)
  const lista = porNombre(catalogo ?? [])

  return (
    <label className="text-sm">
      Requiere especialidad
      <select
        value={valor ?? ''}
        onChange={(e) => onCambiar(e.target.value === '' ? null : e.target.value)}
        className="mt-1 block min-h-10 rounded-md border border-slate-300 bg-white px-2"
      >
        <option value="">Ninguna</option>
        {lista.map((e) => (
          <option key={e.id} value={e.id}>
            {e.nombre}
          </option>
        ))}
      </select>
      {lista.length === 0 && catalogo !== null && (
        <span className="mt-1 block txt-meta">No hay especialidades cargadas todavía.</span>
      )}
    </label>
  )
}
